import { useState, useMemo, useEffect, useRef } from 'react';
import type { Intro as IntroConfig, ContentBlock, AssessmentType, HeroBlock } from '../types';
import { Blocks, Block } from '../components/Blocks';
import { interpolateBlocks } from '../lib/interpolate';
import { HeroBackgroundLayer } from '../components/HeroBackground';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface Props {
  config: IntroConfig;
  /** Top-level AssessmentConfig.surveyMode, threaded in: suppresses scorecard
   *  intro chrome (bottom CTA, live counter, cost-of-inaction, "Free · Instant
   *  results") for a clean survey. */
  surveyMode?: boolean;
  heroImageUrl?: string;
  onStart: () => void;
  sampleSubmissionId?: string;
  hasSampleReport?: boolean;
  slug?: string;
  /** Assessment type — drives which chrome renders. Registration type gets
   *  event-appropriate landing chrome (no scorecard-specific injections or
   *  hardcoded copy). All other types get the standard assessment intro. */
  assessmentType?: AssessmentType;
  /** Full eventConfig object — provides date/time/duration for the event
   *  landing metadata strip and block interpolation. Only used when assessmentType === 'registration'.
   *  The sanitizer injects pre-resolved template vars (eventDayLong, eventDuration, etc.) onto
   *  this object so blocks can reference {{eventDuration}}, {{eventDateOrdinal}}, etc. */
  eventConfig?: Record<string, unknown>;
  /** Lifecycle phase for registration-type events (pre-event | replay | archived).
   *  When 'replay' or 'archived', the standard chrome (sticky nav title, hero
   *  with Register CTA, hardcoded "$10K/month" bottom CTA) is suppressed —
   *  blocks own the full page. Pre-event keeps the chrome. */
  eventPhase?: 'pre-event' | 'replay' | 'archived';
}

/** Format an ISO date string to a readable event date/time string.
 *  Output: "Tuesday, May 12 · 3:00 PM ET" */
function formatEventDate(isoDate: string, timezone?: string): string {
  try {
    const tz = timezone ?? 'UTC';
    const d = new Date(isoDate);
    const day = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long', month: 'long', day: 'numeric' }).format(d);
    const time = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(d);
    return `${day} · ${time}`;
  } catch {
    return '';
  }
}

export default function Intro({ config, surveyMode, heroImageUrl, onStart, sampleSubmissionId, hasSampleReport, slug, assessmentType, eventConfig, eventPhase }: Props) {
  const isRegistration = assessmentType === 'registration';
  // Phase-override mode: render only the blocks. The chrome is registration-
  // semantics-specific and contradicts a replay/archived experience.
  // Also fires when an EventHero is the first block — that block subsumes
  // the dark-hero chrome and Register CTA, so the engine's hardcoded chrome
  // would double-render the same affordances.
  const blocks: ContentBlock[] = config.blocks ?? [];

  // Build event template vars from the pre-resolved eventConfig fields injected by
  // sanitizeConfigForPublic (eventDayLong, eventDuration, eventDateOrdinal, etc.).
  // Used to interpolate {{eventDuration}}, {{eventDayLong}} etc. inside intro blocks.
  const eventVars = useMemo(() => {
    if (!eventConfig) return {} as Record<string, string>;
    const knownKeys = [
      'eventDayLong', 'eventTimeShort', 'eventTimeRange', 'eventDateTimeFull',
      'eventDuration', 'eventDateOrdinal', 'eventWeekday', 'eventDayPart', 'eventDayRelative', 'eventDayRelativeLower',
      'eventUrl', 'bookingUrl', 'replayUrl',
      // Replay-window close vars — auto-derived in sanitizeConfigForPublic from
      // replayLoadedAt + replayWindowDays. Used by the replay-phase eventStrip
      // "Closes" cell + any hero copy that wants "the window closes Saturday".
      'replayClosesShort', 'replayClosesDay', 'replayClosesDate',
      'replayClosesOrdinal', 'replayClosesRelative', 'replayClosesDateTimeFull',
    ];
    const out: Record<string, string> = {};
    for (const k of knownKeys) {
      if (typeof eventConfig[k] === 'string' && eventConfig[k]) out[k] = eventConfig[k] as string;
    }
    return out;
  }, [eventConfig]);
  const hasEventVars = Object.keys(eventVars).length > 0;
  const iBlocks = (b: ContentBlock[]) => hasEventVars ? interpolateBlocks(b, eventVars) : b;

  // EventHero "leads" the page even when an `announceBar` (a thin top
  // chrome bar) precedes it — that's still an event-styled landing. Look
  // for an EventHero in the first 3 blocks before falling back to the
  // strict block[0] check.
  const isEventHeroLed = blocks.slice(0, 3).some((b: ContentBlock) => b.type === 'eventHero');
  // A siteNav-led page has its own navigation chrome — let blocks own the full render.
  const isSiteNavLed = blocks.length > 0 && blocks[0].type === 'siteNav';
  const isPhaseOverride = eventPhase === 'replay' || eventPhase === 'archived' || isEventHeroLed || isSiteNavLed;
  // Prefer static sample route over live submission iframe
  const sampleUrl = hasSampleReport && slug
    ? `/sample/${slug}?preview=1`
    : sampleSubmissionId
      ? `/results/${sampleSubmissionId}?preview=1`
      : null;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [completionCount, setCompletionCount] = useState<number | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  // ── Live counter copy: configurable per assessment, type-aware defaults ──
  const liveCounterCopy = config.liveCounterCopy;
  // surveyMode: a clean survey has no scorecard "X found their gap" counter.
  const liveCounterHidden = surveyMode === true || liveCounterCopy?.hide === true;
  const renderLiveCounter = (count: number) => {
    if (liveCounterHidden) return null;
    // Config-defined copy wins; fall back to type-appropriate defaults
    const defaultSingular = isRegistration
      ? '1 bookkeeper has registered'
      : '1 bookkeeping business owner has already found their gap';
    const defaultPlural = isRegistration
      ? `${count.toLocaleString()} bookkeepers have registered`
      : `${count.toLocaleString()} bookkeeping business owners have already found their gap`;
    const tmpl = count === 1
      ? (liveCounterCopy?.singular ?? defaultSingular)
      : (liveCounterCopy?.plural ?? defaultPlural);
    // Substitute {count} placeholder for the formatted number (singular templates can use it too).
    return tmpl.replace('{count}', count.toLocaleString());
  };

  // ── Bottom CTA copy: configurable per assessment, type-aware defaults ──
  const bottomCta = config.bottomCta;
  // surveyMode: drop the scorecard "$10k/month gap" bottom CTA entirely.
  const bottomCtaHidden = surveyMode === true || bottomCta?.hide === true;
  const bottomCtaHeadline = bottomCta?.headline
    ?? (isRegistration
      ? (config.ctaLabel ?? 'Register Now')
      : `In ${config.estimatedMinutes || 3} minutes, you'll know the one thing\nbetween you and your next $10,000/month.`);
  const bottomCtaBody = bottomCta?.body
    ?? (isRegistration
      ? 'Free live workshop — replay available to all registrants'
      : 'No guesswork. No generic advice. Your specific gap, identified.');

  // Detect if blocks contain a hero block
  const hasHeroBlock = useMemo(
    () => (config.blocks ?? []).some((b: ContentBlock) => b.type === 'hero'),
    [config.blocks],
  );

  // Split blocks: hero+moneyMath go in the dark hero zone, rest below
  const { heroZoneBlocks, belowBlocks, heroBackground } = useMemo(() => {
    const blocks = config.blocks ?? [];
    const heroZone: ContentBlock[] = [];
    const below: ContentBlock[] = [];
    let pastHeroZone = false;

    for (const b of blocks) {
      if (!pastHeroZone && (b.type === 'hero' || b.type === 'moneyMath')) {
        heroZone.push(b);
      } else {
        pastHeroZone = true;
        below.push(b);
      }
    }

    // Lift the background out of the hero block so it can render on the
    // full-width intro-dark-hero container instead of the constrained block-hero card.
    // Strip it from the block itself to prevent a double-render inside HeroRender.
    const heroBlock = heroZone.find((b): b is HeroBlock => b.type === 'hero');
    const bg = heroBlock?.background;
    const liftedBg = bg && bg.type !== 'image' ? bg : undefined;
    const strippedHeroZone = liftedBg
      ? heroZone.map(b => b.type === 'hero' ? { ...b, background: undefined } : b)
      : heroZone;

    return { heroZoneBlocks: strippedHeroZone, belowBlocks: below, heroBackground: liftedBg };
  }, [config.blocks]);

  // Insert cost-of-inaction block after pillarCards, before proofQuotes.
  // Only applies to scorecard/assessment types — never injected for event registrations.
  // Copy is configurable via config.intro.costOfInaction. Defaults preserve
  // current Clients-Scorecard behavior; setting inject:false skips injection
  // entirely (use on any future scorecard that uses pillarCards but doesn't
  // want $10K-specific framing).
  const enrichedBelowBlocks = useMemo(() => {
    if (isRegistration) return belowBlocks;
    const coi = config.costOfInaction;
    if (surveyMode || coi?.inject === false) return belowBlocks;
    const number = coi?.number ?? '$10,000';
    const label = coi?.label ?? 'is what one month of this gap could be costing you';
    const body = coi?.body
      ?? `Every month without clarity on where your gap is, is another month without the clients and revenue on the other side of it. If $10,000/month is where you want to be, that's <strong>$120K a year walking past your business.</strong> The scorecard is free and takes three minutes.`;
    const result: ContentBlock[] = [];
    for (const b of belowBlocks) {
      result.push(b);
      if (b.type === 'pillarCards') {
        result.push({
          type: 'html',
          html: `<div class="block-cost-of-inaction">
            <div class="block-cost-of-inaction-inner">
              <div class="block-cost-of-inaction-number">${number}</div>
              <div class="block-cost-of-inaction-label">${label}</div>
              <p class="block-cost-of-inaction-body">${body}</p>
            </div>
          </div>`,
        } as ContentBlock);
      }
    }
    return result;
  }, [belowBlocks, isRegistration, config.costOfInaction, surveyMode]);

  // Sticky nav on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyNav(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch completion count
  useEffect(() => {
    if (!slug) return;
    fetch(`${API}/api/benchmarks/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.totalRespondents) {
          // Start from a credible base and add real completions
          setCompletionCount(data.totalRespondents);
        }
      })
      .catch(() => {});
  }, [slug]);

  // NOTE: the legacy full-page iframe override was retired 2026-05-29 — every
  // landing now renders through native `.tsb-evt-*` event blocks.

  // Replay / archived phases (or EventHero-led pages) own the full page via
  // their phase-specific blocks — the standard registration chrome (sticky
  // nav, Register CTA hero, hardcoded bottom CTA) doesn't fit a post-event
  // or marketing-landing experience. When an EventHero block leads the array,
  // the .tsb-evt-page class drops the engine's narrow column container so
  // full-bleed sections (hero, navy stats, final CTA) render edge-to-edge.
  if (isPhaseOverride) {
    return (
      <div
        className={`intro-page${isEventHeroLed ? ' tsb-evt-page' : ''}${
          isEventHeroLed && config.pageTheme ? ` tsb-evt-theme-${config.pageTheme}` : ''
        }`}
      >
        <div className="intro-sections">
          <Blocks blocks={iBlocks(blocks)} />
        </div>
      </div>
    );
  }

  return (
    <div className="intro-page">
      {/* ── Sticky nav CTA ── */}
      <div className={`intro-sticky-nav${showStickyNav ? ' visible' : ''}`}>
        <div className="intro-sticky-inner">
          <span className="intro-sticky-title">{config.headline}</span>
          <button className="btn btn-primary btn-sm" onClick={onStart}>
            {config.ctaLabel ?? 'Start'} →
          </button>
        </div>
      </div>

      {hasHeroBlock ? (
        /* ── Dark hero zone: context bar + hero block + money math + CTA ── */
        <>
          <section className="intro-dark-hero" ref={heroRef}>
            {heroBackground && <HeroBackgroundLayer bg={heroBackground} />}
            <div className="intro-dark-hero-inner">
              {/* Hero zone blocks (hero + moneyMath) */}
              <div className="intro-hero-zone-blocks">
                {heroZoneBlocks.map((b, i) => <Block key={i} block={b} />)}
              </div>

              {/* CTA directly after money math */}
              <div className="intro-hero-cta-zone">
                <button className="btn btn-primary btn-lg" onClick={onStart}>
                  {config.ctaLabel ?? (isRegistration ? 'Register Now' : 'Start')} →
                </button>
                {(config as any).trustLine && (
                  <p className="intro-trust">{(config as any).trustLine}</p>
                )}
                {completionCount !== null && completionCount > 0 && !liveCounterHidden && (
                  <div className="intro-live-counter">
                    <span className="intro-live-dot" />
                    {renderLiveCounter(completionCount)}
                  </div>
                )}
              </div>
            </div>

            {/* Scroll cue — the hero fills the viewport, so signal there's more below */}
            <button
              type="button"
              className="intro-hero-scroll-cue"
              aria-label="Scroll to see more"
              onClick={() => heroRef.current?.nextElementSibling?.scrollIntoView({ behavior: 'smooth' })}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </section>

          {/* ── Sample report mockup — immediately after hero ── */}
          {sampleUrl && (
            <section className="intro-results-teaser hero-adjacent">
              <div className="intro-results-teaser-inner">
                <div className="intro-results-teaser-text">
                  <div className="intro-results-teaser-eyebrow">What you'll get</div>
                  <h2 className="intro-results-teaser-headline">
                    Your personalised gap analysis<br />with revenue projections
                  </h2>
                  <p className="intro-results-teaser-body">
                    See exactly which pillar is costing you the most, how you compare to the community,
                    and a dollar-figure projection of what closing your gap could mean — based on data
                    from 43,000+ bookkeeper contacts.
                  </p>
                  <button className="btn btn-secondary" onClick={() => setLightboxOpen(true)}>
                    See a sample report
                  </button>
                </div>
                <div className="intro-results-teaser-preview" onClick={() => setLightboxOpen(true)}>
                  <div className="intro-results-teaser-mockup">
                    <div className="intro-results-teaser-mockup-bar">
                      <span /><span /><span />
                    </div>
                    <div className="intro-results-teaser-mockup-content">
                      <div className="intro-results-teaser-mockup-badge">Your #1 Focus Area</div>
                      <div className="intro-results-teaser-mockup-title">Client Clarity</div>
                      <div className="intro-results-teaser-mockup-score">
                        <div className="intro-results-teaser-mockup-pct">42%</div>
                        <div className="intro-results-teaser-mockup-label">5/12 points</div>
                      </div>
                      <div className="intro-results-teaser-mockup-bars">
                        <div className="intro-results-teaser-mockup-bar-row">
                          <span>Clarity</span>
                          <div className="intro-results-teaser-mockup-track"><div style={{ width: '42%' }} /></div>
                        </div>
                        <div className="intro-results-teaser-mockup-bar-row">
                          <span>Pipeline</span>
                          <div className="intro-results-teaser-mockup-track"><div style={{ width: '67%' }} /></div>
                        </div>
                        <div className="intro-results-teaser-mockup-bar-row">
                          <span>Referrals</span>
                          <div className="intro-results-teaser-mockup-track"><div style={{ width: '58%' }} /></div>
                        </div>
                      </div>
                      <div className="intro-results-teaser-mockup-roi">
                        <div className="intro-results-teaser-mockup-roi-label">Projected revenue impact</div>
                        <div className="intro-results-teaser-mockup-roi-value">+$2,400/mo</div>
                      </div>
                    </div>
                  </div>
                  <div className="intro-results-teaser-click-hint">Click to preview full report</div>
                </div>
              </div>
            </section>
          )}

          {/* ── Below-fold content blocks ── */}
          {enrichedBelowBlocks.length > 0 && (
            <div className="intro-sections">
              <div className="blocks">
                {enrichedBelowBlocks.map((b, i) => <Block key={i} block={b} />)}
              </div>
            </div>
          )}
        </>
      ) : (
        /* ── Standard intro (no hero block) ── */
        <>
          <section className={`intro-hero${isRegistration ? ' intro-hero--event' : ''}`}>
            {heroImageUrl && !isRegistration && (
              <div className="intro-hero-bg" style={{ backgroundImage: `url("${heroImageUrl}")` }} />
            )}
            <div className="intro-hero-inner">
              {/* Event metadata badge — date, duration, cost */}
              {isRegistration && eventConfig?.eventDate && (
                <div className="event-meta-strip">
                  <span className="event-meta-date">{formatEventDate(eventConfig.eventDate as string, eventConfig.eventTimezone as string | undefined)}</span>
                  {eventVars.eventDuration && (
                    <span className="event-meta-item">{eventVars.eventDuration}</span>
                  )}
                  <span className="event-meta-item event-meta-free">Free</span>
                </div>
              )}

              <h1 className="intro-headline">{config.headline}</h1>
              {config.subhead && <p className="intro-subhead">{config.subhead}</p>}
              {config.body && <p className="intro-body">{config.body}</p>}

              <button className="btn btn-primary btn-lg" onClick={onStart}>
                {config.ctaLabel ?? (isRegistration ? 'Register Now' : 'Start')} →
              </button>

              {(config as any).trustLine && (
                <p className="intro-trust">{(config as any).trustLine}</p>
              )}
              {!isRegistration && !surveyMode && config.estimatedMinutes && !((config as any).trustLine) && (
                <p className="intro-trust">
                  ~{config.estimatedMinutes} minute{config.estimatedMinutes === 1 ? '' : 's'} · Free · Instant results
                </p>
              )}
            </div>
          </section>

          {config.blocks && config.blocks.length > 0 && (
            <div className="intro-sections">
              <Blocks blocks={iBlocks(config.blocks)} />
            </div>
          )}
        </>
      )}

      {/* Lightbox */}
      {lightboxOpen && sampleUrl && (
        <div className="intro-lightbox-overlay" onClick={() => setLightboxOpen(false)}>
          <div className="intro-lightbox" onClick={e => e.stopPropagation()}>
            <button className="intro-lightbox-close" onClick={() => setLightboxOpen(false)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
            <iframe
              src={sampleUrl}
              title="Sample Results"
              className="intro-lightbox-iframe"
            />
          </div>
        </div>
      )}

      {/* Bottom CTA — suppressed when config.intro.bottomCta.hide === true.
          Event registrations get the intro-bottom-cta--event modifier for
          transparent bg + on-page text color; scorecards keep the dark version. */}
      {!bottomCtaHidden && (
        <section className={`intro-bottom-cta${isRegistration ? ' intro-bottom-cta--event' : ''}`}>
          <div className="intro-bottom-inner">
            <h2 style={{ whiteSpace: 'pre-line' }}>{bottomCtaHeadline}</h2>
            <p className="intro-trust">{bottomCtaBody}</p>
            <button className="btn btn-primary btn-lg" onClick={onStart}>
              {config.ctaLabel ?? (isRegistration ? 'Register Now' : 'Start')} →
            </button>
            {completionCount !== null && completionCount > 0 && !liveCounterHidden && (
              <div className="intro-live-counter">
                <span className="intro-live-dot" />
                {renderLiveCounter(completionCount)}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
